// src/components/Dogs/AddDogModal.jsx
import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createDog } from '../../redux/slices/Dogs/dogsSlice';

const AddDogModal = ({ isOpen, onClose, onDogAdded }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: '',
    breed: '',
    age: '',
    weight: '',
    gender: 'M',
    specialNeeds: '',
    isVaccinated: false,
    isNeutered: false,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const data = new FormData();
    data.append('name', formData.name);
    data.append('breed', formData.breed);
    data.append('age', formData.age);
    data.append('weight', formData.weight);
    data.append('gender', formData.gender);
    data.append('specialNeeds', formData.specialNeeds);
    data.append('isVaccinated', formData.isVaccinated);
    data.append('isNeutered', formData.isNeutered);
    if (imageFile) {
      data.append('file', imageFile);
    }

    try {
      await dispatch(createDog(data)).unwrap();
      onDogAdded(); // refresh the profile dog list
      onClose();
      // reset form
      setFormData({
        name: '', breed: '', age: '', weight: '', gender: 'M',
        specialNeeds: '', isVaccinated: false, isNeutered: false,
      });
      setImageFile(null);
      setImagePreview('');
    } catch (err) {
      setError(err || t('addDogModal.addError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                <div className="bg-red-600 px-6 py-4 flex justify-between items-center">
                  <Dialog.Title className="text-xl font-semibold text-white">
                    {t('addDogModal.title')}
                  </Dialog.Title>
                  <button onClick={onClose} className="text-white hover:text-gray-200">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  {error && <p className="text-red-600 text-sm">{error}</p>}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('addDogModal.name')}</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('addDogModal.breed')}</label>
                    <input
                      type="text"
                      name="breed"
                      required
                      value={formData.breed}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('addDogModal.age')}</label>
                      <input
                        type="number"
                        name="age"
                        required
                        value={formData.age}
                        onChange={handleChange}
                        className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('addDogModal.weight')}</label>
                      <input
                        type="number"
                        name="weight"
                        value={formData.weight}
                        onChange={handleChange}
                        className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('addDogModal.gender')}</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="M">{t('profile.male')}</option>
                        <option value="F">{t('profile.female')}</option>
                      </select>
                    </div>
                    <div className="flex items-center mt-6">
                      <input
                        type="checkbox"
                        name="isNeutered"
                        checked={formData.isNeutered}
                        onChange={handleChange}
                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">{t('addDogModal.isNeutered')}</label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('addDogModal.specialNeeds')}</label>
                    <textarea
                      name="specialNeeds"
                      rows="2"
                      value={formData.specialNeeds}
                      onChange={handleChange}
                      className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="isVaccinated"
                      checked={formData.isVaccinated}
                      onChange={handleChange}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">{t('addDogModal.isVaccinated')}</label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('addDogModal.dogPhoto')}</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="mt-1 w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                    />
                    {imagePreview && (
                      <img src={imagePreview} alt={t('addDogModal.previewAlt')} className="mt-2 h-32 w-32 object-cover rounded-lg" />
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-300"
                    >
                      {loading ? t('addDogModal.adding') : t('addDogModal.addDog')}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddDogModal;